import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { X } from 'lucide-react';
import { foundation } from '../../../ui/tokens';

const { color } = foundation;

/** The visible round button. */
export const OVERLAY_CLOSE_VISUAL_PX = 36;
/** The tap area around it - a finger is never asked to hit a 36px target. */
export const OVERLAY_CLOSE_TAP_PX = 44;
/** The round button sits this far from the top AND right edge of its panel. */
export const OVERLAY_CLOSE_INSET_PX = 16;
/** Corner placement offset of the 44px tap area ((44 - 36) / 2 = 4px inside the 16px inset). */
const CORNER_OFFSET_PX = OVERLAY_CLOSE_INSET_PX - (OVERLAY_CLOSE_TAP_PX - OVERLAY_CLOSE_VISUAL_PX) / 2;
/** A padded (20px) panel's first row pulls the tap area out by this much: 20 - 8 + 4 = 16. */
const PADDED_BLEED_PX = 8;
/**
 * Space a corner-placed header must keep free on its right so a title never runs under the button:
 * 16 inset + 36 button + 12 breathing room.
 */
export const OVERLAY_CLOSE_RESERVE_PX = OVERLAY_CLOSE_INSET_PX + OVERLAY_CLOSE_VISUAL_PX + 12;

const tapAreaStyle = {
  width: OVERLAY_CLOSE_TAP_PX,
  height: OVERLAY_CLOSE_TAP_PX,
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};

const faceStyle = {
  width: OVERLAY_CLOSE_VISUAL_PX,
  height: OVERLAY_CLOSE_VISUAL_PX,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color.neutral.textSecondary,
  transition: 'background-color 120ms ease, color 120ms ease',
  pointerEvents: 'none',
};

/**
 * THE close button of every pop-up and drawer (KAN-948): a 36px round icon button (lucide `X`)
 * inside a 44x44 tap area, 16px from the top and right of its panel, labelled "Close" for a screen
 * reader - never a visible word, never a bare glyph, never a second shape.
 *
 * It does NOT close anything by itself: `onClick` is the HOST's `requestClose` / `guardedClose`,
 * so the exit beat still plays and the unsaved-changes question (`useUnsavedChangesGuard`) still
 * gets asked. A busy host passes `disabled`.
 *
 * Placement:
 *   - `corner` (default) - absolutely positioned; the panel must be `position: relative` (every
 *     `AnimatedModalShell` panel and `DrawerShell`'s header are). Reserve `OVERLAY_CLOSE_RESERVE_PX`
 *     on the right of the title row.
 *   - `inline` - a flex item at the end of a header row. The row's right padding is 12 and its top
 *     padding 12 (the 44px tap area is the tallest item), which lands the round button on the same
 *     16px / 16px.
 *   - `padded` - the first row of a PADDED (20px) panel: an inline item whose -8px margins pull the
 *     tap area into the padding, so the round button again lands 16px / 16px from the corner and the
 *     row stays as short as its title. Use it where the corner placement would anchor to an inner
 *     positioned wrapper (an `ActionBeatStage` root sits INSIDE the panel padding).
 *
 * Guard: overlayClose.guard.test.js - no other file may draw a lucide `X` as a close button.
 *
 * @param {object} props
 * @param {() => void} props.onClick       the host's guarded close.
 * @param {string} [props.label]           accessible name; defaults to the shared "Close".
 * @param {boolean} [props.disabled]
 * @param {'corner'|'inline'|'padded'} [props.placement]
 * @param {string} [props.testId]
 * @param {boolean} [props.hidden]         visually gone + out of the tab order + inert (a morph's ✕ before it arms).
 * @param {object} [props.style]           merged last (opacity / transform for the morph's reveal).
 */
const OverlayCloseButton = forwardRef(function OverlayCloseButton({
  onClick,
  label,
  disabled = false,
  placement = 'corner',
  testId,
  hidden = false,
  style,
}, ref) {
  const intl = useIntl();
  const [hover, setHover] = useState(false);
  const aria = label || intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' });
  let position = { position: 'relative' };
  if (placement === 'corner') {
    position = { position: 'absolute', top: CORNER_OFFSET_PX, right: CORNER_OFFSET_PX, zIndex: 1 };
  } else if (placement === 'padded') {
    position = { position: 'relative', margin: `-${PADDED_BLEED_PX}px -${PADDED_BLEED_PX}px -${PADDED_BLEED_PX}px 0` };
  }
  return (
    <button
      ref={ref}
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={aria}
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : 0}
      data-testid={testId}
      data-cvx-overlay-close=""
      // index.css: no focus ring for tap / click focus (iOS kept it painted after the first tap), a clear one for keyboard.
      data-cvx-wizard-icon-btn=""
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...tapAreaStyle,
        ...position,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(hidden ? { pointerEvents: 'none' } : null),
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          ...faceStyle,
          backgroundColor: hover && !disabled ? color.neutral.border : color.neutral.surfaceMuted,
          color: hover && !disabled ? color.neutral.text : color.neutral.textSecondary,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <X size={18} strokeWidth={2.25} aria-hidden />
      </span>
    </button>
  );
});

export default OverlayCloseButton;

OverlayCloseButton.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  placement: PropTypes.oneOf(['corner', 'inline', 'padded']),
  testId: PropTypes.string,
  hidden: PropTypes.bool,
  style: PropTypes.object,
};
